import { api, type CursorPage, type Page } from "@/lib/api/client";
import {
  CHAT_ATTACHMENT_ENTITY_TYPE,
  CHAT_ATTACHMENT_PURPOSE,
  type AttachmentPurpose,
  type ChatAttachmentDownload,
  type ChatConversationDetail,
  type ChatConversationFilters,
  type ChatConversationRow,
  type ChatMemberRole,
  type ChatMessageRow,
  type ChatReadReceipt,
  type ChatSearchHit,
  type ChatUnreadCount,
  type DirectoryPersonRow,
  type UploadIntent,
} from "./chat.types";

/**
 * The chat endpoints, one function per route.
 *
 * A transcription of the §14b table and nothing more: no merging, no caching, no mapping onto
 * the kit's view types. Finding "where does the mute call live" should be a search, not a
 * reading exercise, and a layer that quietly reshapes a response is a layer where a wrong field
 * name survives review.
 *
 * EVERY QUERY STRING IS BUILT BY THE CLIENT from an object. A caller concatenating `?q=` is how
 * a Turkish search term or an `&` in a title becomes a request the server reads differently
 * from the one that was intended.
 */

const BASE = "/chat";

export const chatApi = {
  /** OFFSET paged — the rail shows a page, unlike the thread, which walks a cursor. */
  conversations(filters: ChatConversationFilters): Promise<Page<ChatConversationRow>> {
    return api.page<ChatConversationRow>(`${BASE}/conversations`, { params: { ...filters } });
  },

  conversation(id: string): Promise<ChatConversationDetail> {
    return api.get<ChatConversationDetail>(`${BASE}/conversations/${id}`);
  },

  /**
   * Opening a direct conversation.
   *
   * ALWAYS 201, even when the conversation already existed — the server answers with the
   * existing row and sets `meta.existing`. So the caller never has to search the rail first,
   * and two tabs doing this at once cannot produce two conversations between the same pair.
   */
  createDirect(userId: string): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations`, { kind: "DIRECT", userId });
  },

  createGroup(input: { title: string; memberUserIds: string[] }): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations`, {
      kind: "GROUP",
      title: input.title,
      memberUserIds: input.memberUserIds,
    });
  },

  /** §3: the version read with the record travels back, or the server answers 409. */
  rename(id: string, input: { title: string; version: number }): Promise<ChatConversationDetail> {
    return api.patch<ChatConversationDetail>(`${BASE}/conversations/${id}`, input);
  },

  /**
   * `shareHistory` false (the server's default too): the people added see the conversation from the
   * moment they join — «Geçmişi paylaş» sends true (§14b, Sohbet 30).
   */
  addMembers(id: string, userIds: string[], shareHistory = false): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations/${id}/members`, { userIds, shareHistory });
  },

  /**
   * «Sahip yap» / «Sahipliği geri al» (H7, 20 Ekim 2026). The group's owner only; the last owner
   * cannot be demoted (422 `LAST_OWNER`). Ownership is handed over by making somebody owner first.
   */
  setMemberRole(id: string, userId: string, role: ChatMemberRole): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations/${id}/members/${userId}/role`, { role });
  },

  /** Removing yourself IS leaving; the server tells the two apart by the id. */
  removeMember(id: string, userId: string): Promise<void> {
    return api.delete(`${BASE}/conversations/${id}/members/${userId}`);
  },

  archive(id: string): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations/${id}/archive`);
  },

  unarchive(id: string): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations/${id}/unarchive`);
  },

  /** `null` unmutes. The server stores an instant; the client only ever sends a duration. */
  mute(id: string, untilMinutes: number | null): Promise<ChatConversationDetail> {
    return api.post<ChatConversationDetail>(`${BASE}/conversations/${id}/mute`, { untilMinutes });
  },

  /**
   * A page of history, NEWEST FIRST.
   *
   * Cursor paged on purpose: a busy thread is written to while it is being read, and an offset
   * walked across a write skips and repeats messages. The panel renders oldest first, so every
   * caller reverses — that reversal lives in `chat-map.ts`, once.
   */
  messages(
    id: string,
    params: { cursor?: string | undefined; limit?: number | undefined },
  ): Promise<CursorPage<ChatMessageRow>> {
    return api.cursor<ChatMessageRow>(`${BASE}/conversations/${id}/messages`, {
      params: { ...(params.cursor ? { cursor: params.cursor } : {}), limit: params.limit ?? 50 },
    });
  },

  /**
   * Sending, with an idempotency key the CALLER owns.
   *
   * §11: the key identifies a SUBMISSION, not a request. The retry of a send that failed on the
   * network must carry the key the first attempt carried, or the message the server already
   * stored is stored a second time. So the key is a parameter here and is generated where the
   * submission is created — never inside this function, which runs again on every retry.
   */
  send(
    id: string,
    input: { body: string; replyToMessageId?: string; attachmentFileIds?: string[] },
    idempotencyKey: string,
  ): Promise<ChatMessageRow> {
    // Sohbetin mesaj düzeyi yazmaları sessiz: sohbet canlı bağlantıyla güncelleniyor; her mesaj arkadaki
    // ekranı yeniden çekmesin (`RequestOptions.quiet`).
    return api.post<ChatMessageRow>(`${BASE}/conversations/${id}/messages`, input, {
      idempotencyKey,
      quiet: true,
    });
  },

  editMessage(messageId: string, body: string): Promise<ChatMessageRow> {
    return api.patch<ChatMessageRow>(`${BASE}/messages/${messageId}`, { body }, { quiet: true });
  },

  /** A tombstone, not a deletion: the row stays with `body: null` and `redactedAt` set. */
  deleteMessage(messageId: string): Promise<void> {
    return api.delete(`${BASE}/messages/${messageId}`, { quiet: true });
  },

  /**
   * The read cursor only moves forward; the server refuses to walk it back. It also settles the
   * reader's bell rows about this conversation (H4) and says how many — the bell is refetched only
   * when that is not zero.
   */
  markRead(id: string, messageId: string): Promise<ChatReadReceipt> {
    return api.post<ChatReadReceipt>(`${BASE}/conversations/${id}/read`, { messageId }, { quiet: true });
  },

  /**
   * Full-text over the caller's own conversations (§14b): at least two characters, the newest 20
   * hits, each naming the conversation it is in (H6). A two-letter term reaches back 90 days.
   */
  searchMessages(params: { q: string; conversationId?: string; limit?: number }): Promise<ChatSearchHit[]> {
    return api.get<ChatSearchHit[]>(`${BASE}/messages/search`, {
      params: {
        q: params.q,
        limit: params.limit ?? 20,
        ...(params.conversationId ? { conversationId: params.conversationId } : {}),
      },
    });
  },

  /** Writes nothing; publishes one event. Rate limited at 120/min — throttle, never per key. */
  typing(id: string): Promise<void> {
    return api.post(`${BASE}/conversations/${id}/typing`, {}, { quiet: true });
  },

  unreadCount(): Promise<ChatUnreadCount> {
    return api.get<ChatUnreadCount>(`${BASE}/unread-count`);
  },

  /**
   * A signed URL for one attachment, valid until `expiresAt`.
   *
   * The module's OWN download route, not `GET /files/{id}/download`: membership of the
   * conversation is checked here, and the general route stopped answering for files the caller
   * did not upload (§9).
   */
  attachmentDownload(messageId: string, linkId: string): Promise<ChatAttachmentDownload> {
    return api.get<ChatAttachmentDownload>(
      `${BASE}/messages/${messageId}/attachments/${linkId}/download`,
    );
  },

  /** The person picker's source. Read from the directory, which is a core module — always on. */
  people(params: { q?: string; limit?: number }): Promise<Page<DirectoryPersonRow>> {
    return api.page<DirectoryPersonRow>("/directory/people", {
      params: { ...(params.q ? { q: params.q } : {}), limit: params.limit ?? 20, sortBy: "name" },
    });
  },
};

/**
 * Step one and step three of the upload. Step two does not come through here — see below.
 */
export const chatFilesApi = {
  /**
   * The upload-slot catalogue, filtered to the chat subject.
   *
   * §9: this is where a client learns which size, count and mime types the slot accepts —
   * "hangi boyut sınırıyla yüklenebileceğini oradan öğren, sabit kodlama". The constants in
   * `chat.types.ts` are the fallback for the moment before this answers, never the rule.
   */
  purposes(): Promise<AttachmentPurpose[]> {
    return api.get<AttachmentPurpose[]>("/files/purposes", {
      params: { entityType: CHAT_ATTACHMENT_ENTITY_TYPE },
    });
  },

  /**
   * `entityId` is deliberately ABSENT.
   *
   * §9: a chat attachment is uploaded before the message that will carry it exists, so there is
   * nothing to point at yet. The binding happens when `attachmentFileIds` reaches
   * `POST /chat/conversations/{id}/messages`, and it is the chat module — not `POST /files/links`
   * — that performs it. Sending an `entityId` here would be inventing a message id.
   */
  intent(input: { filename: string; contentType: string; byteSize: number }): Promise<UploadIntent> {
    return api.post<UploadIntent>("/files/upload-intent", {
      ...input,
      purpose: CHAT_ATTACHMENT_PURPOSE,
      entityType: CHAT_ATTACHMENT_ENTITY_TYPE,
    });
  },

  complete(fileId: string): Promise<void> {
    return api.post(`/files/${fileId}/complete`, {});
  },
};

